"use client";

import type { DietJsonldDietRead } from "@repo/api/catalog/api/schemas/diet-jsonld-diet-read";
import { useLocale, useTranslations } from "next-intl";
import type { FieldError, Merge } from "react-hook-form";
import { DietAddonsSection } from "@/features/addons/components/diet-addons-section";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { CALENDAR_SECTION_ID } from "@/features/cart/helpers/configure-sections";
import type { CartPricing } from "@/features/cart/types";
import type { OmnibusConfiguredPrices } from "@/features/diets/helpers/omnibus";
import type { DietCatalog, DietCatalogDetail } from "@/features/diets/types";
import type { Globals } from "@/features/globals/types/globals.type";
import { useDietDates } from "@/lib/hooks/use-diet-dates";
import type { ConfigureDietReturnFormType } from "../../forms/configure-diet";
import { CartCalendar } from "../cart-calendar";
import { FormSection } from "./configure-form-section";
import { ConfigureSupervariants } from "./configure-supervariants";
import { ConfigureVariants } from "./configure-variants";
import { DietConfigurationCard } from "./diet-card";

export const translatedError = ({
  error,
  t,
  diet,
  lastPossibleOrderDayFormatted,
}: {
  error?: Merge<FieldError, (FieldError | undefined)[]>;
  t: ReturnType<typeof useTranslations>;
  diet: DietCatalog | DietCatalogDetail | DietJsonldDietRead;
  lastPossibleOrderDayFormatted: string;
}) => {
  if (!error) return;

  let err = "";

  if (error.message?.includes("minDays")) {
    err = t(error.message, {
      days: diet.catering?.days?.min?.toString() || "",
    });
  } else if (error.message?.includes("maxDays")) {
    err = t(error.message, {
      days: diet.catering?.days?.max?.toString() || "",
    });
  } else if (error.message?.includes("minMeals")) {
    err = t(error.message, { meals: diet.minMealTypes?.toString() || "" });
  } else if (error.message?.includes("daysAllowedToOrderInFuture")) {
    err = t(error.message, {
      date: lastPossibleOrderDayFormatted,
    });
  } else {
    err = t(error.message || "");
  }
  return err;
};

export const CustomError = ({
  error,
  wrapper = true,
  t,
  diet,
  lastPossibleOrderDayFormatted,
}: {
  error?: Merge<FieldError, (FieldError | undefined)[]>;
  wrapper?: boolean;
  t: ReturnType<typeof useTranslations>;
  diet: DietCatalog | DietCatalogDetail;
  lastPossibleOrderDayFormatted: string;
}) => {
  const translated = translatedError({
    error,
    t,
    diet,
    lastPossibleOrderDayFormatted,
  });
  if (!translated) return null;

  return wrapper ? <Wrapper>{translated}</Wrapper> : translated;
};

const Wrapper = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-6 font-medium text-destructive text-sm">{children}</p>
);

export const ConfigureVariant = ({
  diet,
  form,
  customError,
}: {
  diet: DietCatalogDetail;
  form: ConfigureDietReturnFormType;
  customError?: string;
}) =>
  diet.variants && diet.variants.length > 0 ? (
    <ConfigureVariants diet={diet} form={form} />
  ) : (
    <ConfigureSupervariants customError={customError} diet={diet} form={form} />
  );

export const ConfigureDietForm = ({
  form,
  diet,
  globals,
  pricing,
  subItemsPricing,
  loading,
  menuPrices,
}: {
  form: ConfigureDietReturnFormType;
  diet: DietCatalogDetail;
  globals?: Globals | null;
  pricing?: CartPricing;
  // Prices of the menu preview header (MPL-1835).
  menuPrices?: OmnibusConfiguredPrices;
  // Addon quote of the configured position, used by the addons section.
  subItemsPricing?: SubItemsPricing | null;
  loading?: boolean;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const { lastPossibleOrderDayFormatted } = useDietDates({
    diet,
    saturdays: true,
    sundays: true,
    locale,
  });

  return (
    <section className="space-y-8">
      <div className="space-y-4">
        <DietConfigurationCard
          diet={diet}
          globals={globals}
          kcal={form.getValues("kcal")}
          menuPrices={menuPrices}
          variant={form.getValues("variant")}
        />
      </div>
      <div className="space-y-8">
        <ConfigureVariant
          customError={
            form.formState.errors.meals
              ? translatedError({
                  error: form.formState.errors.meals,
                  t,
                  diet,
                  lastPossibleOrderDayFormatted,
                })
              : undefined
          }
          diet={diet}
          form={form}
        />
        <FormSection
          error={!!form.formState.errors.dates}
          id={CALENDAR_SECTION_ID}
          label={t("cart.configuration.orderDuration")}
        >
          <>
            {form.formState.errors.dates && (
              <CustomError
                diet={diet}
                error={form.formState.errors.dates}
                lastPossibleOrderDayFormatted={lastPossibleOrderDayFormatted}
                t={t}
              />
            )}
            <CartCalendar
              diet={diet as unknown as DietJsonldDietRead}
              form={form}
              loading={loading}
              pricing={pricing}
            />
          </>
        </FormSection>
        {diet.id && (
          <DietAddonsSection
            cateringName={diet.catering?.name}
            dietDates={form.watch("dates")}
            dietId={diet.id}
            subItemsPricing={subItemsPricing}
          />
        )}
      </div>
    </section>
  );
};
